import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import type { Trip, TripItem, CreateTripInput, UpdateTripInput, CreateTripItemInput, UpdateTripItemPackedInput, ItemPriority } from '../types'

export const useTrips = () => {
  const [trips, setTrips] = useState<Trip[]>([])
  const [tripItems, setTripItems] = useState<TripItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Load trips and trip items from Supabase
  useEffect(() => {
    loadTrips()
    loadTripItems()
  }, [])

  const loadTrips = async () => {
    try {
      setError(null)
      
      const { data, error } = await supabase
        .from('trips')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error

      const mappedTrips: Trip[] = data.map(row => ({
        id: row.id.toString(),
        name: row.name,
        description: row.description,
        tripTypeId: row.trip_type_id.toString(),
        status: row.status as Trip['status'],
        startDate: row.start_date ? new Date(row.start_date) : undefined,
        endDate: row.end_date ? new Date(row.end_date) : undefined,
        createdAt: new Date(row.created_at),
        updatedAt: new Date(row.updated_at),
      }))

      setTrips(mappedTrips)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load trips')
    }
  }

  const loadTripItems = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const { data, error } = await supabase
        .from('trip_items')
        .select('*')
        .order('created_at')

      if (error) throw error

      const mappedTripItems: TripItem[] = data.map(row => ({
        id: row.id.toString(),
        tripId: row.trip_id.toString(),
        itemId: row.item_id.toString(),
        priority: row.priority as ItemPriority,
        packed: row.packed,
        packedAt: row.packed_at ? new Date(row.packed_at) : undefined,
        createdAt: new Date(row.created_at),
      }))

      setTripItems(mappedTripItems)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load trip items')
    } finally {
      setLoading(false)
    }
  }

  const createTrip = async (input: CreateTripInput) => {
    try {
      setError(null)
      
      // Get current user or use temp user
      const { data: { user } } = await supabase.auth.getUser()
      const userId = user?.id || 'temp-user-id'
      
      const { data, error } = await supabase
        .from('trips')
        .insert({
          user_id: userId,
          trip_type_id: parseInt(input.tripTypeId),
          name: input.name,
          description: input.description,
          start_date: input.startDate?.toISOString().split('T')[0] || null,
          end_date: input.endDate?.toISOString().split('T')[0] || null,
        })
        .select()
        .single()

      if (error) throw error

      const newTrip: Trip = {
        id: data.id.toString(),
        name: data.name,
        description: data.description,
        tripTypeId: data.trip_type_id.toString(),
        status: data.status as Trip['status'],
        startDate: data.start_date ? new Date(data.start_date) : undefined,
        endDate: data.end_date ? new Date(data.end_date) : undefined,
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at),
      }

      setTrips(prev => [newTrip, ...prev])
      return newTrip
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create trip'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }

  const updateTrip = async (input: UpdateTripInput) => {
    try {
      setError(null)
      
      const { data, error } = await supabase
        .from('trips')
        .update({
          name: input.name,
          description: input.description,
          status: input.status,
          start_date: input.startDate?.toISOString().split('T')[0],
          end_date: input.endDate?.toISOString().split('T')[0],
        })
        .eq('id', parseInt(input.id))
        .select()
        .single()

      if (error) throw error

      const updatedTrip: Trip = {
        id: data.id.toString(),
        name: data.name,
        description: data.description,
        tripTypeId: data.trip_type_id.toString(),
        status: data.status as Trip['status'],
        startDate: data.start_date ? new Date(data.start_date) : undefined,
        endDate: data.end_date ? new Date(data.end_date) : undefined,
        createdAt: new Date(data.created_at),
        updatedAt: new Date(data.updated_at),
      }

      setTrips(prev => 
        prev.map(trip => 
          trip.id === input.id ? updatedTrip : trip
        )
      )
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update trip'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }

  const deleteTrip = async (id: string) => {
    try {
      setError(null)
      
      const { error } = await supabase
        .from('trips')
        .delete()
        .eq('id', parseInt(id))

      if (error) throw error

      setTrips(prev => prev.filter(trip => trip.id !== id))
      // Trip items will be automatically deleted due to CASCADE
      setTripItems(prev => prev.filter(ti => ti.tripId !== id))
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete trip'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }

  const addItemToTrip = async (input: CreateTripItemInput) => {
    try {
      setError(null)
      
      const { data, error } = await supabase
        .from('trip_items')
        .insert({
          trip_id: parseInt(input.tripId),
          item_id: parseInt(input.itemId),
          priority: input.priority,
        })
        .select()
        .single()

      if (error) throw error

      const newTripItem: TripItem = {
        id: data.id.toString(),
        tripId: data.trip_id.toString(),
        itemId: data.item_id.toString(),
        priority: data.priority as ItemPriority,
        packed: data.packed,
        packedAt: data.packed_at ? new Date(data.packed_at) : undefined,
        createdAt: new Date(data.created_at),
      }

      setTripItems(prev => [...prev, newTripItem])
      return newTripItem
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add item to trip'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }

  const updateTripItemPacked = async (input: UpdateTripItemPackedInput) => {
    try {
      setError(null)
      
      const { data, error } = await supabase
        .from('trip_items')
        .update({
          packed: input.packed,
          packed_at: input.packed ? new Date().toISOString() : null,
        })
        .eq('id', parseInt(input.id))
        .select()
        .single()

      if (error) throw error

      const updatedTripItem: TripItem = {
        id: data.id.toString(),
        tripId: data.trip_id.toString(),
        itemId: data.item_id.toString(),
        priority: data.priority as ItemPriority,
        packed: data.packed,
        packedAt: data.packed_at ? new Date(data.packed_at) : undefined,
        createdAt: new Date(data.created_at),
      }

      setTripItems(prev => 
        prev.map(ti => 
          ti.id === input.id ? updatedTripItem : ti
        )
      )
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update trip item'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }

  const removeItemFromTrip = async (tripItemId: string) => {
    try {
      setError(null)
      
      const { error } = await supabase
        .from('trip_items')
        .delete()
        .eq('id', parseInt(tripItemId))

      if (error) throw error

      setTripItems(prev => prev.filter(ti => ti.id !== tripItemId))
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove item from trip'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }

  const getTripItemsForTrip = (tripId: string) => {
    return tripItems.filter(ti => ti.tripId === tripId)
  }

  const createTripFromTripType = async (tripTypeItems: { itemId: string, priority: ItemPriority }[], tripInput: CreateTripInput) => {
    try {
      // Create the trip first
      const newTrip = await createTrip(tripInput)
      
      // Add all items from trip type to the new trip
      const promises = tripTypeItems.map(tti => 
        addItemToTrip({
          tripId: newTrip.id,
          itemId: tti.itemId,
          priority: tti.priority
        })
      )
      
      await Promise.all(promises)
      return newTrip
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create trip from trip type'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }

  const getPackingStats = (tripId: string) => {
    const items = getTripItemsForTrip(tripId)
    const packed = items.filter(item => item.packed).length
    const total = items.length
    const percentage = total > 0 ? Math.round((packed / total) * 100) : 0
    
    return { packed, total, percentage }
  }

  return {
    trips,
    tripItems,
    loading,
    error,
    createTrip,
    updateTrip,
    deleteTrip,
    addItemToTrip,
    updateTripItemPacked,
    removeItemFromTrip,
    getTripItemsForTrip,
    createTripFromTripType,
    getPackingStats,
    refetch: () => {
      loadTrips()
      loadTripItems()
    },
  }
}